import { CAN_I_INTEGRATE_WITH_SHOPIFY_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalIntegrations from "@faire/web-api--source/routes/brand-portal/integrations";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ProductImportWithShopifyIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’re experiencing issues with the <BoldFormatting>product import</BoldFormatting> feature of your Shopify integration, follow these steps to troubleshoot:"
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Introduction to troubleshooting product imports with Shopify",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your Shopify dashboard and go to <BoldFormatting>Publish products</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "First step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="From the <BoldFormatting>Settings</BoldFormatting> page, click <LinkFormatting>Integrations</LinkFormatting> to go to the <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> sales channel in <BoldFormatting>Shopify</BoldFormatting>."
              values={{
                LinkFormatting: IntegrationsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "Second step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once on Shopify and in the Faire sales channel, go to <BoldFormatting>Publish products</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to the <BoldFormatting>Product Linking</BoldFormatting> tab, and check for any outstanding product linking issues. If there are issues, a notification banner will appear at the top of the page."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Verify that the correct <BoldFormatting>tags</BoldFormatting> are selected when importing products. Since you can import products by tag category, ensure that products to sync are tagged, and that you’re selecting the correct tag to import."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Confirm that the product is available for sale in the <BoldFormatting>Faire</BoldFormatting> sales channel:"
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Sixth step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="a">
            <Typography>
              <StrictLocalMsg
                defaultMessage="Navigate to the product page on Shopify."
                description={{
                  text: "First substep of sixth step to troubleshoot product imports",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="b">
            <Typography>
              <StrictLocalMsg
                defaultMessage="Check the list of <BoldFormatting>enabled sales channels</BoldFormatting> on the right side of the product page. A green circle shows the channel is enabled, while a red circle indicates the product is not enabled for Faire."
                values={{
                  BoldFormatting,
                }}
                description={{
                  text: "Second substep of sixth step to troubleshoot product imports",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <Row>
          <Spacer width="2x" />
          <LiItem step="c">
            <Typography>
              <StrictLocalMsg
                defaultMessage="If the product is not enabled, select <BoldFormatting>Manage sales channels</BoldFormatting> and choose <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> from the available channel list."
                values={{
                  BoldFormatting,
                }}
                description={{
                  text: "Third substep of sixth step to troubleshoot product imports",
                }}
              />
            </Typography>
          </LiItem>
        </Row>
        <Spacer height="1x" />
        <LiItem step={7}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Ensure that the product has all the required information to import to Faire, such as images, description, title, and price."
              description={{
                text: "Seventh step to troubleshoot product imports",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If these steps don’t resolve your product import issue, contact our support team for more help."
          description={{
            text: "Conclusion for troubleshooting product imports",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For more detailed information on Shopify product imports, visit <LinkFormatting>Can I integrate with Shopify</LinkFormatting> and see the <BoldFormatting>Catalog Import</BoldFormatting> section."
          values={{
            LinkFormatting: ArticleLinkFormatting,
            BoldFormatting,
          }}
          description={{
            text: "Link to the article on how Shopify price sync works",
          }}
        />
      </Typography>
    </Column>
  );
};

const IntegrationsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalIntegrations()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(CAN_I_INTEGRATE_WITH_SHOPIFY_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
